import React from 'react';

export interface ApiKeyMetricRow {
  apiKey: string;
  pending: number;
  processingTps: number | null;
}

export interface UrlMetricRow {
  url: string;
  status: 'UP' | 'DOWN' | '—';
  postRatePerSec: number | null;
  avgLatencyMs: string;
  maxLatencyMs: string;
}

interface MetricsSummaryProps {
  apiKeyRows: ApiKeyMetricRow[];
  urlRows: UrlMetricRow[];
  lastFetch: string | null;
  loading: boolean;
  error: string | null;
}

export function MetricsSummary({
  apiKeyRows,
  urlRows,
  lastFetch,
  loading,
  error,
}: MetricsSummaryProps) {
  return (
    <div className="metrics-summary">
      <div className="metrics-summary-header">
        <h3>Live summary</h3>
        <span className="metrics-raw-meta">
          Auto-refresh every 10s while this tab is open
          {lastFetch ? ` · Last fetch: ${lastFetch}` : ''}
          {loading ? ' · Updating…' : ''}
        </span>
      </div>
      {error && <p className="metrics-raw-error">{error}</p>}

      <h4 className="metrics-summary-subtitle">API keys (processing)</h4>
      <p className="metrics-summary-hint">
        Processing TPS from <code>apikey_messages_processed_messages_total</code> over 30s.
        Keys with no pending backlog and no recent processing are hidden.
      </p>
      {apiKeyRows.length === 0 ? (
        <p className="metrics-summary-empty">No active API keys right now.</p>
      ) : (
        <div className="metrics-table-wrap">
          <table className="metrics-table">
            <thead>
              <tr>
                <th>API key</th>
                <th>Pending</th>
                <th>Processing TPS (30s)</th>
              </tr>
            </thead>
            <tbody>
              {apiKeyRows.map((row) => (
                <tr key={row.apiKey}>
                  <td>{row.apiKey}</td>
                  <td>{row.pending.toLocaleString()}</td>
                  <td>{row.processingTps !== null ? row.processingTps.toFixed(1) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4 className="metrics-summary-subtitle">Backend URLs</h4>
      <p className="metrics-summary-hint">
        POST rate from <code>url_processing_time_seconds_count</code>; latency from sum/count and max.
      </p>
      {urlRows.length === 0 ? (
        <p className="metrics-summary-empty">No active backend URLs in the last window.</p>
      ) : (
        <div className="metrics-table-wrap">
          <table className="metrics-table">
            <thead>
              <tr>
                <th>URL</th>
                <th>Status</th>
                <th>POST/s (30s)</th>
                <th>Avg latency (30s)</th>
                <th>Max (scrape)</th>
              </tr>
            </thead>
            <tbody>
              {urlRows.map((row) => (
                <tr key={row.url}>
                  <td className="metrics-url-cell" title={row.url}>
                    {row.url}
                  </td>
                  <td>
                    <span
                      className={`metrics-status ${
                        row.status === 'UP'
                          ? 'metrics-status-up'
                          : row.status === 'DOWN'
                            ? 'metrics-status-down'
                            : 'metrics-status-unknown'
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td>{row.postRatePerSec !== null ? row.postRatePerSec.toFixed(2) : '—'}</td>
                  <td>{row.avgLatencyMs}</td>
                  <td>{row.maxLatencyMs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
